<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';

import { useMapGetter } from 'dashboard/composables/store';
import internalChatAPI from 'dashboard/api/internalChat';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const messages = ref([]);
const members = ref([]);
const message = ref('');
const loading = ref(true);
const sending = ref(false);
const error = ref('');

const messagesContainer = ref(null);
const textareaRef = ref(null);
const fileInput = ref(null);

const selectedFiles = ref([]);
const replyTo = ref(null);
const selectedMentionIds = ref([]);

const currentUserId = useMapGetter('getCurrentUserID');

const canSend = computed(() => {
  return (
    !sending.value &&
    (message.value.trim().length > 0 || selectedFiles.value.length > 0)
  );
});

const mentionQuery = computed(() => {
  const text = message.value;
  const cursor =
    textareaRef.value?.selectionStart ?? text.length;

  const beforeCursor = text.slice(0, cursor);
  const match = beforeCursor.match(/(?:^|\s)@([^\s@]*)$/);

  return match ? match[1].toLowerCase() : null;
});

const mentionSuggestions = computed(() => {
  if (mentionQuery.value === null) return [];

  const query = mentionQuery.value;

  return members.value
    .filter(member => {
      const name = String(member.name || '').toLowerCase();
      const email = String(member.email || '').toLowerCase();

      return name.includes(query) || email.includes(query);
    })
    .slice(0, 8);
});

const scrollToBottom = async () => {
  await nextTick();

  if (messagesContainer.value) {
    messagesContainer.value.scrollTop =
      messagesContainer.value.scrollHeight;
  }
};

const appendMessage = item => {
  if (!item?.id) return;

  const exists = messages.value.some(
    existing => Number(existing.id) === Number(item.id)
  );

  if (!exists) {
    messages.value.push(item);
  }
};

const loadMessages = async ({ scroll = false } = {}) => {
  try {
    const response = await internalChatAPI.getMessages();
    messages.value = Array.isArray(response.data)
      ? response.data
      : [];

    if (scroll) {
      await scrollToBottom();
    }
  } catch (e) {
    error.value = 'Não foi possível carregar o Chat Interno.';
  } finally {
    loading.value = false;
  }
};

const loadMembers = async () => {
  try {
    const response = await internalChatAPI.getMembers();
    members.value = Array.isArray(response.data)
      ? response.data
      : [];
  } catch (e) {
    members.value = [];
  }
};

const markAsRead = async () => {
  try {
    await internalChatAPI.markRead();
    emitter.emit(BUS_EVENTS.INTERNAL_CHAT_READ);
  } catch (e) {
    // A falha de leitura não deve interromper o chat.
  }
};

const onInternalChatMessageCreated = async data => {
  appendMessage(data);
  await scrollToBottom();

  if (Number(data?.user?.id) !== Number(currentUserId.value)) {
    await markAsRead();
  }
};

const getInitials = name => {
  return String(name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
};

const formatTime = value => {
  if (!value) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const formatFileSize = bytes => {
  const size = Number(bytes || 0);

  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const isImage = file => {
  return String(file?.content_type || '').startsWith('image/');
};

const isCurrentUserMentioned = item => {
  const ids = Array.isArray(item?.mentioned_user_ids)
    ? item.mentioned_user_ids
    : [];

  return ids
    .map(Number)
    .includes(Number(currentUserId.value));
};

const setReply = item => {
  replyTo.value = item;

  nextTick(() => {
    textareaRef.value?.focus();
  });
};

const clearReply = () => {
  replyTo.value = null;
};

const selectMention = member => {
  const text = message.value;
  const cursor =
    textareaRef.value?.selectionStart ?? text.length;

  const beforeCursor = text.slice(0, cursor);
  const atPosition = beforeCursor.lastIndexOf('@');

  if (atPosition < 0) return;

  const beforeMention = text.slice(0, atPosition);
  const afterCursor = text.slice(cursor);

  const inserted = `@${member.name} `;

  message.value =
    `${beforeMention}${inserted}${afterCursor}`;

  if (!selectedMentionIds.value.includes(member.id)) {
    selectedMentionIds.value.push(member.id);
  }

  const newCursor =
    beforeMention.length + inserted.length;

  nextTick(() => {
    textareaRef.value?.focus();
    textareaRef.value?.setSelectionRange(
      newCursor,
      newCursor
    );
  });
};

const mentionedIdsForSend = () => {
  return selectedMentionIds.value.filter(id => {
    const member = members.value.find(
      item => Number(item.id) === Number(id)
    );

    if (!member) return false;

    return message.value.includes(`@${member.name}`);
  });
};

const openFilePicker = () => {
  fileInput.value?.click();
};

const onFileChange = event => {
  const incoming = Array.from(event.target.files || []);

  incoming.forEach(file => {
    const exists = selectedFiles.value.some(
      current =>
        current.name === file.name &&
        current.size === file.size &&
        current.lastModified === file.lastModified
    );

    if (!exists) {
      selectedFiles.value.push(file);
    }
  });

  event.target.value = '';
};

const removeSelectedFile = index => {
  selectedFiles.value.splice(index, 1);
};

const sendMessage = async () => {
  if (!canSend.value) return;

  sending.value = true;
  error.value = '';

  try {
    const response = await internalChatAPI.sendMessage({
      content: message.value.trim(),
      replyToId: replyTo.value?.id || null,
      mentionedUserIds: mentionedIdsForSend(),
      files: selectedFiles.value,
    });

    appendMessage(response.data);

    message.value = '';
    selectedFiles.value = [];
    selectedMentionIds.value = [];
    replyTo.value = null;

    await scrollToBottom();

    nextTick(() => {
      textareaRef.value?.focus();
    });
  } catch (e) {
    error.value =
      e?.response?.data?.message ||
      'Não foi possível enviar a mensagem.';
  } finally {
    sending.value = false;
  }
};

onMounted(async () => {
  emitter.on(
    BUS_EVENTS.INTERNAL_CHAT_MESSAGE_CREATED,
    onInternalChatMessageCreated
  );

  await Promise.all([
    loadMessages({ scroll: true }),
    loadMembers(),
  ]);

  await markAsRead();
});

onBeforeUnmount(() => {
  emitter.off(
    BUS_EVENTS.INTERNAL_CHAT_MESSAGE_CREATED,
    onInternalChatMessageCreated
  );
});
</script>

<template>
  <div class="flex flex-col flex-1 w-full min-w-0 h-full bg-n-background">
    <header
      class="flex items-center px-8 border-b h-[72px] shrink-0 border-n-weak"
    >
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          Chat Interno RC
        </h1>

        <p class="text-sm text-n-slate-10">
          Comunicação interna da equipe
        </p>
      </div>
    </header>

    <div
      ref="messagesContainer"
      class="flex-1 overflow-y-auto px-8 py-6"
    >
      <div
        v-if="loading"
        class="flex items-center justify-center h-full text-n-slate-10"
      >
        Carregando mensagens...
      </div>

      <div
        v-else-if="messages.length === 0"
        class="flex items-center justify-center h-full text-n-slate-10"
      >
        Nenhuma mensagem ainda. Inicie a conversa.
      </div>

      <div
        v-else
        class="flex flex-col w-full max-w-[1400px] gap-4 mx-auto"
      >
        <div
          v-for="item in messages"
          :key="item.id"
          class="flex items-end gap-2 group"
          :class="
            Number(item.user.id) === Number(currentUserId)
              ? 'flex-row-reverse'
              : 'flex-row'
          "
        >
          <div
            class="flex items-center justify-center w-9 h-9 overflow-hidden rounded-full shrink-0 bg-n-alpha-3 text-xs font-semibold text-n-slate-11"
          >
            <img
              v-if="item.user.avatar_url"
              :src="item.user.avatar_url"
              :alt="item.user.name"
              class="object-cover w-full h-full"
            />

            <span v-else>
              {{ getInitials(item.user.name) }}
            </span>
          </div>

          <div
            class="relative max-w-[80%] min-w-[180px] rounded-2xl px-4 py-3"
            :class="[
              Number(item.user.id) === Number(currentUserId)
                ? 'bg-n-brand text-white rounded-br-md'
                : 'bg-n-alpha-2 text-n-slate-12 rounded-bl-md',
              isCurrentUserMentioned(item)
                ? 'ring-2 ring-n-brand/40'
                : '',
            ]"
          >
            <div
              class="flex items-center gap-2 mb-1.5 text-xs"
              :class="
                Number(item.user.id) === Number(currentUserId)
                  ? 'text-white/80'
                  : 'text-n-slate-10'
              "
            >
              <span class="font-medium">
                {{ item.user.name }}
              </span>

              <span>
                {{ formatTime(item.created_at) }}
              </span>

              <span
                v-if="isCurrentUserMentioned(item)"
                class="font-semibold"
              >
                @ você
              </span>
            </div>

            <div
              v-if="item.reply_to"
              class="mb-2 px-3 py-2 rounded-lg border-l-2"
              :class="
                Number(item.user.id) === Number(currentUserId)
                  ? 'bg-white/10 border-white/50'
                  : 'bg-n-alpha-2 border-n-brand'
              "
            >
              <div class="text-xs font-semibold opacity-80">
                {{ item.reply_to.user.name }}
              </div>

              <div
                v-if="item.reply_to.content"
                class="text-xs opacity-80 line-clamp-2"
              >
                {{ item.reply_to.content }}
              </div>

              <div
                v-else-if="item.reply_to.files_count"
                class="text-xs opacity-80"
              >
                Anexo
              </div>
            </div>

            <div
              v-if="item.content"
              class="text-[15px] leading-6 whitespace-pre-wrap break-words"
            >
              {{ item.content }}
            </div>

            <div
              v-if="item.files?.length"
              class="grid gap-2 mt-3"
            >
              <template
                v-for="file in item.files"
                :key="file.id"
              >
                <a
                  v-if="isImage(file)"
                  :href="file.file_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="block overflow-hidden rounded-lg"
                >
                  <img
                    :src="file.file_url"
                    :alt="file.filename"
                    class="block max-h-[420px] max-w-full object-contain rounded-lg"
                  />
                </a>

                <a
                  v-else
                  :href="file.file_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-3 p-3 rounded-lg border border-current/15 no-underline"
                >
                  <span class="i-lucide-file-text size-5 shrink-0" />

                  <span class="min-w-0">
                    <span
                      class="block text-sm font-medium truncate"
                    >
                      {{ file.filename }}
                    </span>

                    <span class="block text-xs opacity-70">
                      {{ formatFileSize(file.byte_size) }}
                    </span>
                  </span>
                </a>
              </template>
            </div>

            <button
              type="button"
              class="absolute opacity-0 group-hover:opacity-100 transition-opacity -top-3 px-2 py-1 rounded-md bg-n-background border border-n-weak text-xs text-n-slate-11 shadow-sm"
              :class="
                Number(item.user.id) === Number(currentUserId)
                  ? 'left-2'
                  : 'right-2'
              "
              @click="setReply(item)"
            >
              Responder
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="error"
      class="px-8 py-2 text-sm text-red-600 border-t border-n-weak"
    >
      {{ error }}
    </div>

    <footer class="px-8 py-5 border-t shrink-0 border-n-weak">
      <form
        class="w-full max-w-[1400px] mx-auto"
        @submit.prevent="sendMessage"
      >
        <div
          v-if="replyTo"
          class="flex items-center justify-between gap-4 px-4 py-2 mb-2 rounded-lg bg-n-alpha-2 border-l-2 border-n-brand"
        >
          <div class="min-w-0">
            <div class="text-xs font-semibold text-n-brand">
              Respondendo a {{ replyTo.user.name }}
            </div>

            <div class="text-xs truncate text-n-slate-10">
              {{
                replyTo.content ||
                (replyTo.files?.length
                  ? 'Mensagem com anexo'
                  : 'Mensagem')
              }}
            </div>
          </div>

          <button
            type="button"
            class="i-lucide-x size-4 text-n-slate-10 shrink-0"
            title="Cancelar resposta"
            @click="clearReply"
          />
        </div>

        <div
          v-if="selectedFiles.length"
          class="flex flex-wrap gap-2 mb-2"
        >
          <div
            v-for="(file, index) in selectedFiles"
            :key="`${file.name}-${file.size}-${index}`"
            class="flex items-center gap-2 max-w-[260px] px-3 py-2 rounded-lg bg-n-alpha-2"
          >
            <span class="i-lucide-paperclip size-4 shrink-0" />

            <span class="text-xs truncate">
              {{ file.name }}
            </span>

            <button
              type="button"
              class="i-lucide-x size-3 shrink-0"
              @click="removeSelectedFile(index)"
            />
          </div>
        </div>

        <div class="flex items-end gap-3">
          <input
            ref="fileInput"
            type="file"
            multiple
            class="hidden"
            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
            @change="onFileChange"
          />

          <button
            type="button"
            class="flex items-center justify-center w-12 h-12 rounded-xl border border-n-strong text-n-slate-11 hover:bg-n-alpha-2 shrink-0"
            title="Anexar arquivo"
            @click="openFilePicker"
          >
            <span class="i-lucide-paperclip size-5" />
          </button>

          <div class="relative flex-1">
            <textarea
              ref="textareaRef"
              v-model="message"
              maxlength="10000"
              rows="2"
              autocomplete="off"
              placeholder="Digite uma mensagem... Use @ para mencionar alguém"
              class="block w-full min-h-12 max-h-32 resize-y px-4 py-3 rounded-xl border border-n-strong bg-n-background text-n-slate-12 outline-none focus:border-n-brand"
              @keydown.enter.exact.prevent="sendMessage"
            />

            <div
              v-if="mentionSuggestions.length"
              class="absolute z-50 bottom-full left-0 mb-2 w-full max-w-md overflow-hidden rounded-xl border border-n-weak bg-n-background shadow-lg"
            >
              <button
                v-for="member in mentionSuggestions"
                :key="member.id"
                type="button"
                class="flex items-center w-full gap-3 px-3 py-2.5 text-left hover:bg-n-alpha-2"
                @click="selectMention(member)"
              >
                <div
                  class="flex items-center justify-center w-8 h-8 overflow-hidden rounded-full bg-n-alpha-3 text-xs font-semibold shrink-0"
                >
                  <img
                    v-if="member.avatar_url"
                    :src="member.avatar_url"
                    :alt="member.name"
                    class="object-cover w-full h-full"
                  />

                  <span v-else>
                    {{ getInitials(member.name) }}
                  </span>
                </div>

                <span class="min-w-0">
                  <span
                    class="block text-sm font-medium truncate text-n-slate-12"
                  >
                    {{ member.name }}
                  </span>

                  <span
                    class="block text-xs truncate text-n-slate-10"
                  >
                    {{ member.email }}
                  </span>
                </span>
              </button>
            </div>
          </div>

          <button
            type="submit"
            :disabled="!canSend"
            class="h-12 px-6 rounded-xl bg-n-brand text-white font-medium disabled:opacity-50 shrink-0"
          >
            {{ sending ? 'Enviando...' : 'Enviar' }}
          </button>
        </div>

        <div class="mt-1.5 text-[11px] text-n-slate-9">
          Enter envia • Shift + Enter quebra a linha • @ menciona um colega
        </div>
      </form>
    </footer>
  </div>
</template>
